/**
 * Centralised HTTP client for the Aliens OS gateway.
 *
 * - Same-origin cookies carry the session; no token or credential ever lives in JS.
 * - Every mutating request carries the CSRF header the gateway requires.
 * - Errors are normalised into `ApiError` so UI code handles one shape.
 */
import { env } from '@/config/env'
import { fromCompat, isSignInPath, pathKind, resolveApiPath } from '@/api/paths'

export interface ApiSuccess<T> {
  ok: true
  data: T
  /** Dispatch's envelope `id` — the new transaction id for the Add endpoints that answer data:null. */
  id?: string | number | null
  data2?: unknown
  summary?: unknown
  message?: string | null
  meta?: { total: number; page: number; pageSize: number }
}

export type ApiErrorCode =
  | 'UNAUTHENTICATED'
  | 'SESSION_EVICTED'
  | 'FORBIDDEN'
  | 'VALIDATION_FAILED'
  | 'UPSTREAM_REJECTED'
  | 'UPSTREAM_TIMEOUT'
  | 'UPSTREAM_UNAVAILABLE'
  | 'RATE_LIMITED'
  | 'NETWORK_ERROR'
  | 'OFFLINE'
  | 'ABORTED'
  | (string & {})

export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly details?: unknown
  readonly requestId?: string
  constructor(status: number, code: ApiErrorCode, message: string, extra?: { details?: unknown; requestId?: string }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = extra?.details
    this.requestId = extra?.requestId
  }
  get isAuth() {
    return this.status === 401
  }
  get isRetryable() {
    return this.status === 0 || this.status === 429 || this.status >= 502
  }
}

type Listener = (err: ApiError) => void
const authListeners = new Set<Listener>()
/** The auth store subscribes here to react to expired / evicted sessions globally. */
export const onAuthError = (fn: Listener) => (authListeners.add(fn), () => authListeners.delete(fn))

let currentLanguage: 'ar' | 'en' = 'ar'
let currentSectionId: string | undefined
export const setRequestLanguage = (lang: 'ar' | 'en') => (currentLanguage = lang)
export const setRequestSection = (sectionId?: string) => (currentSectionId = sectionId)

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Return the raw Response (binary downloads). */
  raw?: boolean
  /** Give up after this long with a TIMEOUT error instead of waiting for ever (owner, 2026-10-13: a search spinning 10 minutes). */
  timeoutMs?: number
  headers?: Record<string, string>
  /** Direct mode: hand back the compat answer as the backend sent it (the sign-in reads its two-step answers itself). */
  rawEnvelope?: boolean
  /** The path is the URL path itself (a public backend route such as /public/v1/...), not under the API base. */
  absolute?: boolean
}

/** The address of a web path in this build's mode: for links, images and downloads the browser opens itself. */
export const apiUrl = (path: string) => resolveApiPath(path, env.apiMode, env.apiBase)

/** A random id this browser keeps, naming the device a request came from; empty where storage is unavailable. */
export function deviceId(): string {
  try {
    let id = localStorage.getItem('aos.device') ?? ''
    if (!id) {
      id = `d${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`
      localStorage.setItem('aos.device', id)
    }
    return id
  } catch {
    return ''
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<ApiSuccess<T>> {
  const res = await send(path, opts)
  const body = (await res.json().catch(() => null)) as ApiSuccess<T> | null
  if (!body) throw new ApiError(res.status, 'INVALID_RESPONSE', 'Unexpected response from server')
  return body
}

export async function requestRaw(path: string, opts: RequestOptions = {}): Promise<Response> {
  return send(path, { ...opts, raw: true })
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const method = opts.method ?? (opts.body === undefined ? 'GET' : 'POST')
  const direct = env.apiMode === 'direct'
  // direct mode: the compat answer is unwrapped here as the gateway's proxy did (docs/NO_GATEWAY.md)
  const compat = direct && pathKind(path) === 'dispatch' && !opts.absolute
  const url = new URL(opts.absolute ? path : resolveApiPath(path, env.apiMode, env.apiBase), window.location.origin)
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))

  const headers: Record<string, string> = { Accept: 'application/json', 'Accept-Language': currentLanguage, ...opts.headers }
  // the CSRF header: the gateway's check, and in direct mode the backend's for every call its session cookie signs
  if (method !== 'GET') headers['x-aliens-os'] = '1'
  // the compat API reads the language from its own header (the gateway translated Accept-Language)
  if (compat) headers.Language = currentLanguage
  if (currentSectionId) headers['x-section-id'] = currentSectionId
  // which device a scan came from, so the cameras know the desk (gateway ext/camScans.ts)
  const device = deviceId()
  if (device) headers['x-aos-device'] = device

  let body: BodyInit | undefined
  if (opts.body instanceof FormData) body = opts.body
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(opts.body)
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiError(0, 'OFFLINE', 'You are offline')
  }

  const timer = opts.timeoutMs ? AbortSignal.timeout(opts.timeoutMs) : undefined
  const signal = timer && opts.signal ? AbortSignal.any([opts.signal, timer]) : (timer ?? opts.signal)
  let res: Response
  try {
    res = await fetch(url, { method, headers, body, credentials: 'include', signal })
  } catch (err) {
    if (timer?.aborted && !opts.signal?.aborted) throw new ApiError(0, 'TIMEOUT', `لم يرد الخادم خلال ${Math.round(opts.timeoutMs! / 1000)} ثانية — أعد المحاولة أو قلل الفترة`)
    if ((err as Error).name === 'AbortError') throw new ApiError(0, 'ABORTED', 'Request cancelled')
    throw new ApiError(0, 'NETWORK_ERROR', 'Network error — check your connection')
  }

  if (compat && !opts.rawEnvelope) return compatAnswer(res, path)
  if (res.ok || opts.rawEnvelope) return res

  const payload = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string; details?: unknown }; requestId?: string } | null
  const err = new ApiError(res.status, payload?.error?.code ?? `HTTP_${res.status}`, payload?.error?.message ?? res.statusText ?? 'Request failed', {
    details: payload?.error?.details,
    requestId: payload?.requestId,
  })
  if (err.status === 401 && !isSignInPath(path)) authListeners.forEach((fn) => fn(err))
  throw err
}

/**
 * A compat answer (direct mode) as the screens read the gateway's proxy: a file passes as it is; JSON becomes
 * `{ ok: true, data, ... }`, or the ApiError the proxy raised (422 for `success: false`, 401 ends the session).
 */
async function compatAnswer(res: Response, path: string): Promise<Response> {
  const type = res.headers.get('content-type') ?? ''
  if (res.ok && !type.includes('json')) return res
  const body = (await res.json().catch(() => null)) as unknown
  const out = fromCompat(res.status, body, path)
  if (out.ok) return new Response(JSON.stringify(out), { status: 200, headers: { 'content-type': 'application/json' } })
  const err = new ApiError(out.status, out.error.code, out.error.message, { details: out.error.details, requestId: res.headers.get('x-request-id') ?? undefined })
  if (err.status === 401 && !isSignInPath(path)) authListeners.forEach((fn) => fn(err))
  throw err
}
